// AuthForms.js — Formulários de autenticação reaproveitados em três lugares:
//   /login (página Entrar), /criar-conta (página Criar Conta) e o checkout.
// Manter um único formulário evita que as regras (senha mínima, telefone,
// verificação de e-mail) fiquem divergindo entre as telas.
import React, { useState, useCallback } from "react";
import { useAuth } from "../../services/context/AuthContext";
import { campoCls, Campo } from "../ui/Formulario";
import GoogleButton, { mostrarBlocoGoogle } from "./GoogleButton";
import useProfileData from "../../hooks/userProfileData";

// O estilo do campo vem de `ui/Formulario.js`, o mesmo que o perfil e o
// checkout usam. Esta cópia local era byte a byte igual à do perfil.
export const inputCls = campoCls;

// `mb-4` é o espaçamento entre campos DESTES formulários — os do perfil ficam
// dentro de grades com `gap`, os daqui são irmãos soltos dentro de um <form>.
// Por isso o apelido em vez do uso direto: o primitivo não carrega margem.
export const Field = ({ className = "", ...props }) => (
  <Campo className={"mb-4 " + className} {...props} />
);

const Erro = ({ children }) => children ? <p className="text-red-400 text-sm mb-3">{children}</p> : null;

const Divisor = () => (
  <div className="flex items-center gap-3 my-5">
    <span className="h-px flex-1 bg-white/10" />
    <span className="text-xs uppercase tracking-wider text-white/35">ou</span>
    <span className="h-px flex-1 bg-white/10" />
  </div>
);

/**
 * Bloco do Google + divisor. `texto` muda o rótulo do botão do Google
 * ("Fazer login com" na tela de entrar, "Inscrever-se com" na de cadastro).
 *
 * `onPrecisaTelefone` é opcional: quem passa a prop sabe mostrar o passo do
 * telefone (FormCompletarTelefone) e recebe o desvio; quem não passa continua
 * caindo direto em `onSucesso`, exatamente como antes.
 */
const BlocoGoogle = ({ onSucesso, onPrecisaTelefone, texto }) => {
  const { loginComGoogle } = useAuth();
  const [erro, setErro] = useState("");
  const [entrando, setEntrando] = useState(false);

  const handleCredential = useCallback(
    async (credential) => {
      setErro("");
      setEntrando(true);
      try {
        // Conta criada pelo Google nasce sem telefone: o Google não entrega
        // número e o cadastro por e-mail é o único lugar do site que exige um.
        // O backend devolve `precisa_completar_cadastro` justamente para este
        // desvio; enquanto ninguém lia a flag, quem entrava pelo social ficava
        // sem telefone para sempre e a editora não tinha como falar do pedido.
        const dados = await loginComGoogle(credential);
        if (dados?.precisa_completar_cadastro && onPrecisaTelefone) onPrecisaTelefone();
        else onSucesso();
      } catch (e) {
        setErro(e.message || "Não foi possível entrar com o Google.");
      } finally {
        setEntrando(false);
      }
    },
    [loginComGoogle, onSucesso, onPrecisaTelefone]
  );

  // Enquanto o Client ID do Google não estiver configurado, o bloco inteiro
  // (botão + divisor) some em produção — melhor nada do que um botão morto.
  if (!mostrarBlocoGoogle) return null;

  return (
    <>
      <GoogleButton onCredential={handleCredential} onError={setErro} texto={texto} />
      {entrando && <p className="text-white/60 text-sm mt-2 text-center">Entrando com o Google...</p>}
      <Erro>{erro}</Erro>
      <Divisor />
    </>
  );
};

// ===================== Entrar =====================
export const FormEntrar = ({ onSucesso, onPrecisaTelefone }) => {
  const { login } = useAuth();
  const [dados, setDados] = useState({ email: "", password: "" });
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setErro("");
    try {
      setEnviando(true);
      await login(dados.email, dados.password);
      onSucesso();
    } catch {
      setErro("E-mail e/ou senha incorretos.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <BlocoGoogle onSucesso={onSucesso} onPrecisaTelefone={onPrecisaTelefone} texto="signin_with" />
      <form onSubmit={submeter}>
        <Field label="E-mail">
          <input
            type="email"
            value={dados.email}
            onChange={(e) => setDados((s) => ({ ...s, email: e.target.value }))}
            placeholder="voce@email.com"
            className={inputCls}
            autoComplete="email"
            required
          />
        </Field>
        <Field label="Senha">
          <input
            type="password"
            value={dados.password}
            onChange={(e) => setDados((s) => ({ ...s, password: e.target.value }))}
            placeholder="Sua senha"
            className={inputCls}
            autoComplete="current-password"
            required
          />
        </Field>
        <Erro>{erro}</Erro>
        <button
          type="submit"
          disabled={enviando}
          className="w-full rounded-lg bg-insigne-orange text-paper py-3 font-semibold hover:bg-insigne-orange-dark transition-colors disabled:opacity-60"
        >
          {enviando ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </>
  );
};

// ===================== Criar conta =====================
// Em caso de sucesso NÃO conclui direto: devolve o e-mail para a etapa de
// verificação por código (FormVerificarEmail).
export const FormCriarConta = ({ onSucesso, onPrecisaVerificar, onPrecisaTelefone }) => {
  const { signup } = useAuth();
  const [dados, setDados] = useState({ nome: "", email: "", telefone: "", password: "", repetir: "" });
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setErro("");

    if (dados.password.length < 8) return setErro("A senha deve ter no mínimo 8 caracteres.");
    if (dados.password !== dados.repetir) return setErro("As senhas não coincidem.");
    if ((dados.telefone || "").replace(/\D/g, "").length < 10) {
      return setErro("Informe um telefone válido com DDD (ex.: (84) 99999-9999).");
    }

    try {
      setEnviando(true);
      await signup({
        nome: dados.nome,
        email: dados.email,
        telefone: dados.telefone,
        password: dados.password,
      });
      onPrecisaVerificar(dados.email);
    } catch {
      setErro("Não foi possível criar a conta. Verifique os dados e tente novamente.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <BlocoGoogle onSucesso={onSucesso} onPrecisaTelefone={onPrecisaTelefone} texto="signup_with" />
      <form onSubmit={submeter}>
        <Field label="Nome completo">
          <input
            type="text"
            value={dados.nome}
            onChange={(e) => setDados((s) => ({ ...s, nome: e.target.value }))}
            placeholder="Seu nome completo"
            className={inputCls}
            autoComplete="name"
            required
          />
        </Field>
        <Field label="E-mail">
          <input
            type="email"
            value={dados.email}
            onChange={(e) => setDados((s) => ({ ...s, email: e.target.value }))}
            placeholder="voce@email.com"
            className={inputCls}
            autoComplete="email"
            required
          />
        </Field>
        <Field label="Telefone (com DDD)">
          <input
            type="tel"
            value={dados.telefone}
            onChange={(e) => setDados((s) => ({ ...s, telefone: e.target.value }))}
            placeholder="(00) 00000-0000"
            className={inputCls}
            autoComplete="tel"
            required
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Senha" dica="Mínimo de 8 caracteres">
            <input
              type="password"
              value={dados.password}
              onChange={(e) => setDados((s) => ({ ...s, password: e.target.value }))}
              placeholder="Crie uma senha"
              className={inputCls}
              autoComplete="new-password"
              required
            />
          </Field>
          <Field label="Repetir senha">
            <input
              type="password"
              value={dados.repetir}
              onChange={(e) => setDados((s) => ({ ...s, repetir: e.target.value }))}
              placeholder="Repita a senha"
              className={inputCls}
              autoComplete="new-password"
              required
            />
          </Field>
        </div>
        <Erro>{erro}</Erro>
        <button
          type="submit"
          disabled={enviando}
          className="w-full rounded-lg bg-insigne-orange text-paper py-3 font-semibold hover:bg-insigne-orange-dark transition-colors disabled:opacity-60"
        >
          {enviando ? "Criando conta..." : "Criar conta"}
        </button>
      </form>
    </>
  );
};

// ===================== Confirmar e-mail (código de 6 dígitos) =====================
export const FormVerificarEmail = ({ email, onSucesso }) => {
  const { verificarEmail, reenviarCodigo } = useAuth();
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState("");
  const [info, setInfo] = useState("Enviamos um código de 6 dígitos para o seu e-mail.");
  const [verificando, setVerificando] = useState(false);
  const [reenviando, setReenviando] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setErro("");
    const cod = codigo.replace(/\D/g, "");
    if (cod.length !== 6) return setErro("Digite o código de 6 dígitos enviado por e-mail.");
    try {
      setVerificando(true);
      await verificarEmail(cod);
      onSucesso();
    } catch (err) {
      setErro(err.message || "Código inválido.");
    } finally {
      setVerificando(false);
    }
  };

  const reenviar = async () => {
    setErro("");
    setInfo("");
    try {
      setReenviando(true);
      await reenviarCodigo();
      setInfo("Novo código enviado para o seu e-mail.");
    } catch (err) {
      setErro(err.message || "Não foi possível reenviar.");
    } finally {
      setReenviando(false);
    }
  };

  return (
    <div>
      <h2 className="titulo-display text-[2.3rem] mb-2">Confirme seu e-mail</h2>
      <p className="text-white/70 text-sm mb-6">
        Enviamos um código de 6 dígitos para{" "}
        <span className="text-white font-semibold break-all">{email}</span>. Digite-o abaixo para ativar sua conta.
      </p>
      <form onSubmit={submeter}>
        <Field label="Código de verificação">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
            placeholder="------"
            className={inputCls + " tracking-[0.5em] text-center text-lg font-bold"}
            autoFocus
          />
        </Field>
        {info && <p className="text-emerald-400 text-sm mb-3">{info}</p>}
        <Erro>{erro}</Erro>
        <button
          type="submit"
          disabled={verificando}
          className="w-full rounded-lg bg-insigne-orange text-paper py-3 font-semibold hover:bg-insigne-orange-dark transition-colors disabled:opacity-60"
        >
          {verificando ? "Verificando..." : "Confirmar e ativar conta"}
        </button>
      </form>
      <button
        onClick={reenviar}
        disabled={reenviando}
        className="mt-4 text-sm text-insigne-orange hover:underline disabled:opacity-60"
      >
        {reenviando ? "Reenviando..." : "Reenviar código"}
      </button>
    </div>
  );
};

// ============ Telefone depois do primeiro login social ============
// Existe porque o Google não devolve telefone: quem entra por ele cria conta
// sem número, e no painel a venda desse cliente aparece sem contato nenhum.
// Pedir aqui é o caminho mais barato — reaproveita o campo e a regra de 10
// dígitos do cadastro e não põe atrito no checkout, hoje o fluxo mais leve do
// site.
//
// O passo é PULÁVEL de propósito: a pessoa acabou de se autenticar e já tem
// direito de entrar. Travar aqui trocaria "célula vazia no painel" por
// "cliente que não passa da tela de login". Quem pular vê o pedido de novo no
// próximo login social (o backend recalcula a flag a cada entrada) — é o custo
// aceito para não perder o número de vez.
//
// Gravar usa o MESMO `updateProfile` do perfil (PUT dados-usuario-editora),
// que já traduz o 422 do backend em mensagem legível. Custo: o hook dispara um
// GET do perfil ao montar cuja resposta esta tela descarta — uma requisição a
// mais logo após o login, em troca de não duplicar aqui a gravação e o
// tratamento de erro que já existem.
export const FormCompletarTelefone = ({ onConcluir }) => {
  const { updateProfile } = useProfileData();
  const [telefone, setTelefone] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setErro("");
    // Mesma validação do cadastro: 10 dígitos = DDD + número de fixo.
    if (telefone.replace(/\D/g, "").length < 10) {
      return setErro("Informe um telefone válido com DDD (ex.: (84) 99999-9999).");
    }
    try {
      setSalvando(true);
      await updateProfile({ telefone });
      onConcluir();
    } catch (err) {
      // Falha ao gravar NÃO bloqueia o login: mostramos o motivo e o "Agora
      // não" continua ali, levando a pessoa para dentro do site com a conta
      // que ela acabou de autenticar.
      setErro(
        (err?.message || "Não foi possível salvar o telefone agora.") +
          " Você pode tentar de novo ou seguir e cadastrar depois em Meus dados."
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <h2 className="titulo-display text-[2.3rem] mb-2">Só falta o seu telefone</h2>
      <p className="text-white/70 text-sm mb-6">
        É por ele que a editora fala com você sobre o seu pedido: confirmação, envio e
        qualquer problema com a entrega. Não usamos para divulgação.
      </p>
      <form onSubmit={submeter}>
        <Field label="Telefone (com DDD)">
          <input
            type="tel"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            placeholder="(00) 00000-0000"
            className={inputCls}
            autoComplete="tel"
            autoFocus
          />
        </Field>
        <Erro>{erro}</Erro>
        <button
          type="submit"
          disabled={salvando}
          className="w-full rounded-lg bg-insigne-orange text-paper py-3 font-semibold hover:bg-insigne-orange-dark transition-colors disabled:opacity-60"
        >
          {salvando ? "Salvando..." : "Salvar e continuar"}
        </button>
      </form>
      {/* Discreto de propósito: é saída, não a ação principal da tela. */}
      <button
        type="button"
        onClick={onConcluir}
        disabled={salvando}
        className="mt-4 text-sm text-white/60 hover:text-white hover:underline disabled:opacity-60"
      >
        Agora não
      </button>
    </div>
  );
};
